import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { FONT_FAMILY } from '../common/sharedFunctions';

/* ─── CSS injectée une seule fois ──────────────────────────────────────── */
const STYLE_ID = 'animated-nav-keyframes';
if (!document.getElementById(STYLE_ID)) {
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes navLineGrow {
      from { height: 0; }
      to   { height: 100%; }
    }
    @keyframes navDotPop {
      0%   { transform: scale(0); opacity: 0; }
      70%  { transform: scale(1.3); }
      100% { transform: scale(1); opacity: 1; }
    }
    @keyframes navDotPulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(212,175,55,0.5); }
      50%       { box-shadow: 0 0 0 8px rgba(212,175,55,0); }
    }
    @keyframes navLabelSlide {
      from { opacity: 0; transform: translateX(10px); }
      to   { opacity: 1; transform: translateX(0); }
    }
  `;
  document.head.appendChild(style);
}

/* ─── Sections de la landing page ──────────────────────────────────────── */
const SECTIONS = [
  { id: 'section-hero',         label: 'Accueil',        icon: '🏠' },
  { id: 'section-services',     label: 'Services',       icon: '🚗' },
  { id: 'section-eats',         label: 'Eats',           icon: '🍽️' },
  { id: 'section-about',        label: 'À propos',       icon: '✦' },
  { id: 'section-fleet',        label: 'Notre flotte',   icon: '⚡' },
  { id: 'section-testimonials', label: 'Avis clients',   icon: '⭐' },
  { id: 'section-faq',          label: 'FAQ',            icon: '❓' },
  { id: 'section-download',     label: 'Application',    icon: '📱' },
];

const GOLD = '#D4AF37';
const GOLD_DIM = 'rgba(212,175,55,0.15)';
const GOLD_BORDER = 'rgba(212,175,55,0.3)';

/* ─── Composant ─────────────────────────────────────────────────────────── */
const AnimatedSectionNav = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [mounted, setMounted] = useState(false);
  const rafRef = useRef(null);

  // Monté progressivement
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 800);
    return () => clearTimeout(t);
  }, []);

  // Scroll tracker : calcule le progrès global + section active
  const onScroll = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docHeight > 0 ? Math.min(scrollTop / docHeight, 1) : 0;
      setProgress(pct);

      // Section active
      let bestIdx = 0;
      SECTIONS.forEach((s, idx) => {
        const el = document.getElementById(s.id);
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.top <= window.innerHeight * 0.45) bestIdx = idx;
      });
      setActiveIndex(bestIdx);
    });
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [onScroll]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        right: { xs: 'auto', md: 24 },
        left: { xs: 8, md: 'auto' },
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 999,
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0,
        opacity: mounted ? 1 : 0,
        transition: 'opacity 0.6s ease',
        pointerEvents: mounted ? 'auto' : 'none'
      }}
    >
      {/* Ligne de fond (grise) */}
      <Box sx={{
        position: 'absolute',
        top: 0, bottom: 0,
        left: '50%', transform: 'translateX(-50%)',
        width: 2,
        background: 'rgba(255,255,255,0.08)',
        borderRadius: 1,
        zIndex: 0
      }} />

      {/* Ligne de progression (or) */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: '50%', transform: 'translateX(-50%)',
        width: 2,
        height: `${progress * 100}%`,
        background: `linear-gradient(to bottom, ${GOLD} 0%, rgba(212,175,55,0.4) 100%)`,
        borderRadius: 1,
        zIndex: 1,
        transition: 'height 0.15s linear',
        boxShadow: `0 0 8px rgba(212,175,55,0.5)`
      }} />

      {/* Points de navigation */}
      {SECTIONS.map((section, idx) => {
        const isActive = idx === activeIndex;
        const isPast = idx < activeIndex;

        return (
          <Box
            key={section.id}
            sx={{
              position: 'relative', zIndex: 2,
              display: 'flex', alignItems: 'center',
              py: 1.5, cursor: 'pointer',
              // Afficher le label au hover
              '&:hover .nav-label': {
                opacity: 1,
                transform: 'translateX(0)',
                pointerEvents: 'auto'
              }
            }}
            onClick={() => scrollTo(section.id)}
          >
            {/* Dot */}
            <Box sx={{
              width: isActive ? 14 : isPast ? 8 : 8,
              height: isActive ? 14 : isPast ? 8 : 8,
              borderRadius: '50%',
              backgroundColor: isActive ? GOLD : isPast ? 'rgba(212,175,55,0.5)' : 'rgba(255,255,255,0.2)',
              border: isActive ? `2px solid ${GOLD}` : isPast ? '1px solid rgba(212,175,55,0.4)' : '1px solid rgba(255,255,255,0.15)',
              transition: 'all 0.3s cubic-bezier(.22,.68,0,1.2)',
              animation: isActive ? 'navDotPulse 2.5s infinite' : 'none',
              boxShadow: isActive ? `0 0 12px rgba(212,175,55,0.6)` : 'none',
              flexShrink: 0
            }} />

            {/* Label flottant à gauche */}
            <Box
              className="nav-label"
              sx={{
                position: 'absolute',
                right: 22,
                top: '50%', transform: 'translateY(-50%) translateX(8px)',
                backgroundColor: 'rgba(0,0,0,0.85)',
                backdropFilter: 'blur(8px)',
                border: `1px solid ${isActive ? GOLD : GOLD_BORDER}`,
                borderRadius: '8px',
                px: 1.5, py: 0.6,
                whiteSpace: 'nowrap',
                opacity: 0,
                pointerEvents: 'none',
                transition: 'opacity 0.25s ease, transform 0.25s ease',
                display: 'flex', alignItems: 'center', gap: 0.8
              }}
            >
              <Typography sx={{ fontSize: '13px', lineHeight: 1 }}>
                {section.icon}
              </Typography>
              <Typography sx={{
                fontFamily: FONT_FAMILY,
                fontSize: '12px',
                fontWeight: isActive ? 700 : 500,
                color: isActive ? GOLD : 'rgba(255,255,255,0.85)',
                letterSpacing: '0.03em'
              }}>
                {section.label}
              </Typography>
            </Box>
          </Box>
        );
      })}

      {/* Indicateur de progression % */}
      <Box sx={{
        mt: 2, zIndex: 2,
        backgroundColor: GOLD_DIM,
        border: `1px solid ${GOLD_BORDER}`,
        borderRadius: '20px', px: 1.2, py: 0.4,
        opacity: progress > 0.02 ? 1 : 0,
        transition: 'opacity 0.3s ease'
      }}>
        <Typography sx={{
          fontFamily: FONT_FAMILY, fontSize: '10px',
          fontWeight: 700, color: GOLD, letterSpacing: '0.04em'
        }}>
          {Math.round(progress * 100)}%
        </Typography>
      </Box>
    </Box>
  );
};

export default AnimatedSectionNav;
